// Client-side A/B testing rails — NOT wired into any page yet.
//
// This is the browser mirror of the server's lib/abTesting.js: the SAME
// subject + experiment always resolves to the SAME variant on both sides,
// because both hash `${experiment}:${subject}` with SHA-256 and map the
// first 4 bytes onto the weighted variant list. If you change one, change
// both (the server's tests/ab-testing.test.js pins the algorithm).
//
// How an onboarding experiment will use this at go-live:
//
//   const anonId = getAnonId();
//   const variant = await assignVariant(anonId, "onboarding-cta", ["control", "big-button"]);
//   logAbEvent({ event: "exposure", experiment: "onboarding-cta", variant, anonId });
//   // ...later, when the goal happens:
//   logAbEvent({ event: "conversion", experiment: "onboarding-cta", variant, anonId });
//
// Results appear automatically in Admin → Health → A/B Experiments.

import api from "../api";

export type VariantInput = string | { name: string; weight?: number };

const ANON_ID_KEY = "requesta-anon-id";

// Module-level cache: even when localStorage is blocked, the id stays stable
// for the lifetime of the page — a per-CALL id would count one fan as many
// exposures and skew every experiment's denominators.
let cachedAnonId: string | null = null;

function makeId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // randomUUID is secure-context-only (and absent on iOS Safari ≤15.3).
    // Analytics identity, not security — a Math.random id is fine here.
    return `anon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

// Stable per-browser anonymous id for fans (who have no accounts). Survives
// reloads via localStorage; a cleared browser is simply a new subject.
export function getAnonId(): string {
  if (cachedAnonId) return cachedAnonId;
  try {
    const existing = localStorage.getItem(ANON_ID_KEY);
    if (existing) {
      cachedAnonId = existing;
      return existing;
    }
    const fresh = makeId();
    localStorage.setItem(ANON_ID_KEY, fresh);
    cachedAnonId = fresh;
    return fresh;
  } catch {
    // Storage blocked (private-mode edge cases): page-lifetime fallback.
    cachedAnonId = makeId();
    return cachedAnonId;
  }
}

// Deterministic variant assignment — must stay byte-identical to the server's
// lib/abTesting.js. Async because SubtleCrypto is.
export async function assignVariant(
  subjectId: string,
  experimentName: string,
  variants: VariantInput[]
): Promise<string | null> {
  if (!subjectId || !experimentName || !Array.isArray(variants) || variants.length === 0) {
    return null;
  }
  const normalized = variants.map((v) =>
    typeof v === "string" ? { name: v, weight: 1 } : { name: v.name, weight: v.weight ?? 1 }
  );
  const totalWeight = normalized.reduce(
    (sum, v) => sum + (Number.isFinite(v.weight) && v.weight > 0 ? v.weight : 0),
    0
  );
  if (totalWeight <= 0) return null;

  const bytes = new TextEncoder().encode(`${experimentName}:${subjectId}`);
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
  // First 4 digest bytes as an UNSIGNED 32-bit big-endian int. The final
  // `>>> 0` must wrap the WHOLE expression — JS bitwise ops yield signed
  // int32, and a signed (negative) roll would silently collapse every
  // high-byte hash into the first variant, diverging from the server.
  const roll =
    (((digest[0] << 24) | (digest[1] << 16) | (digest[2] << 8) | digest[3]) >>> 0) / 0x100000000;

  let cumulative = 0;
  for (const v of normalized) {
    const w = Number.isFinite(v.weight) && v.weight > 0 ? v.weight : 0;
    cumulative += w / totalWeight;
    if (roll < cumulative) return v.name;
  }
  for (let i = normalized.length - 1; i >= 0; i--) {
    if ((normalized[i].weight ?? 0) > 0) return normalized[i].name;
  }
  return null;
}

export interface AbEventInput {
  event: string;
  experiment?: string;
  variant?: string | null;
  anonId?: string;
  props?: Record<string, unknown>;
}

// Fire-and-forget event logging. Analytics must NEVER break the product
// flow it observes — all failures are swallowed.
export function logAbEvent(input: AbEventInput): void {
  api.post("/ab/events", input).catch(() => {});
}
